import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { detectInAppBrowser, isStandalone } from "@/lib/device";
import { getPushState, isSubscribedOnThisDevice } from "@/lib/push-client";
import { profileCoreProgress } from "@/lib/profile-progress";
import { isLightInstall, nextStep, type NextStep, type NextStepFacts } from "@/lib/next-step";
import type { Profile } from "@/lib/people-constants";

type P = Profile & { app_installed_at?: string | null };

export const pushCountKey = (uid: string | undefined) =>
  ["push-subscriptions", "count", uid] as const;

/** Факты и итоговый шаг. `ready=false`, пока не знаем браузер/подписки — ничего не показываем. */
export function useNextStep() {
  const { user } = useAuth();
  const uid = user?.id;
  const [env, setEnv] = useState<{ inApp: boolean; standalone: boolean; pushHere: boolean } | null>(
    null,
  );

  const refreshHere = async () => {
    const here =
      getPushState() === "granted" ? await isSubscribedOnThisDevice().catch(() => false) : false;
    setEnv({ inApp: !!detectInAppBrowser(), standalone: isStandalone(), pushHere: here });
  };
  useEffect(() => {
    void refreshHere();
  }, []);

  const { data: profile } = useQuery({
    queryKey: ["profile", uid],
    enabled: !!uid,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", uid!)
        .single();
      if (error) throw error;
      return data as P;
    },
  });

  const { data: pushCount, refetch } = useQuery({
    queryKey: pushCountKey(uid),
    enabled: !!uid,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("push_subscriptions")
        .select("id", { count: "exact", head: true })
        .eq("user_id", uid!);
      if (error) return 0;
      return count ?? 0;
    },
  });

  const progress = profileCoreProgress(profile);
  const ready = !!uid && !!profile && !!env && pushCount !== undefined;
  const facts: NextStepFacts | null = ready
    ? {
        inApp: env!.inApp,
        standalone: env!.standalone,
        installedProfile: !!profile!.app_installed_at,
        pushAny: (pushCount ?? 0) > 0,
        pushHere: env!.pushHere,
        profileMissing: progress.missing.length > 0,
      }
    : null;
  const step: NextStep | undefined = facts ? nextStep(facts) : undefined;

  return {
    uid,
    profile,
    ready,
    step,
    light: facts ? isLightInstall(facts) : false,
    standalone: !!env?.standalone,
    missing: progress.missing.length,
    refresh: async () => {
      await refreshHere();
      await refetch();
    },
  };
}
